import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Label,
  Text,
  Select,
  DatePicker,
  DragDropFileList,
  DragDropFileFileProps,
} from '@mercai/clever'

import { getFileNameAndExtension } from '@/helpers'
import { ORDERS_CENTER_PAYMENT_URL, api } from '@/services'

import { OrderPayment } from '@/components'
import { ModalFinanceValidation } from '../modal-finance-validation'

import type { FinancePaymentsFormProps } from './payment.type'
import type { OrderPaymentProps, SaleOrderProps } from '@/types'
import { FinanceValidationFormEnum } from '../modal-finance-validation/modal-finance-validation.type'

import styles from './payment.module.css'

export const FinancePaymentTab = ({
  onChange,
  values = {} as SaleOrderProps,
  onSuccessUploadFiles,
}: FinancePaymentsFormProps) => {
  const { t } = useTranslation('componentOrderCenterForm')
  const { t: tEnums } = useTranslation('enums')

  const [dataFilesProofMap, setDataFilesProofMap] = useState<
    Record<string, DragDropFileFileProps[]>
  >({})

  const [selectedPaymentIndex, setSelectedPaymentIndex] = useState<number>()

  const [financeValidationFormOpen, setFinanceValidationFormOpen] = useState<
    FinanceValidationFormEnum | undefined
  >(undefined)

  const handleChange = (
    keyIndex: number,
    keyParam: keyof OrderPaymentProps,
    value: string,
  ) => {
    const payments = [...values?.payments]

    payments[keyIndex] = {
      ...payments[keyIndex],
      [keyParam]: value,
    }

    onChange({
      ...values,
      payments,
    })
  }

  const currencySaleOrder = values?.payments?.[0]?.currency
  const dollarExchangeRate = values?.payments?.[0]?.dollarExchangeRate // verifify

  const getCurrencyValue = (value: string = '0') => {
    if (currencySaleOrder === 'USD') {
      return +value
    }

    if (currencySaleOrder === 'MXN') {
      return Number(value) * Number(dollarExchangeRate || 0)
    }

    return 0
  }

  const validatePayment = (
    keyIndex: number,
    paymentUuid: string,
  ): { isValid: boolean; error?: FinanceValidationFormEnum } => {
    const totalOrderPrice = getCurrencyValue(values?.totalOrderPrice)
    const totalPaid = getCurrencyValue(values?.totalPaidValue)

    if (totalOrderPrice > totalPaid) {
      return {
        isValid: false,
        error: FinanceValidationFormEnum['total-ammount'],
      }
    }

    if (!dataFilesProofMap?.[paymentUuid]?.[keyIndex]) {
      return {
        isValid: false,
        error: FinanceValidationFormEnum['payment-proof'],
      }
    }

    return { isValid: true }
  }

  const onChangePaymentValues = ({
    paymentUuid,
    keyIndex,
    keyParam,
    value,
  }: {
    paymentUuid: string
    keyIndex: number
    keyParam: keyof OrderPaymentProps
    value: string
  }) => {
    if (keyParam === 'status' && value === 'paid') {
      const { isValid, error } = validatePayment(keyIndex, paymentUuid)

      if (isValid) {
        handleChange(keyIndex, keyParam, value)
        return
      }

      setSelectedPaymentIndex(keyIndex)
      setFinanceValidationFormOpen(FinanceValidationFormEnum[error!])
      return
    }

    handleChange(keyIndex, keyParam, value)
  }

  const handleCloseValidationModal = async (forceChange?: boolean) => {
    if (forceChange && selectedPaymentIndex !== undefined) {
      handleChange(selectedPaymentIndex, 'status', 'paid')
      setSelectedPaymentIndex(undefined)
    }

    setFinanceValidationFormOpen(undefined)
  }

  const getPaymentTitle = (form: string, installment: number) => {
    if (form !== 'partial') {
      return `${t(`paymentTitle`)} - ${tEnums(`finance_payment_form_${form}`)}`
    }

    if (values?.payments?.length > 2) {
      return `${t('paymentPartialTitle')} - ${installment} de ${values?.payments?.length}`
    }
    return `${installment === 1 ? t('paymentUpfrontTitle') : t('paymentOnDeliveryTitle')}`
  }

  useEffect(() => {
    const newDataFilesProofMap: Record<string, DragDropFileFileProps[]> = {}

    values?.payments?.forEach((payment) => {
      if (payment.proof) {
        const file = getFileNameAndExtension(payment.proof)
        newDataFilesProofMap[payment.uuid] = [
          {
            id: file.fileName,
            name: `${file.fileName}.${file.extension}`,
            url: payment.proof,
            uploaded: true,
          },
        ]
      } else {
        newDataFilesProofMap[payment.uuid] = []
      }
    })

    setDataFilesProofMap(newDataFilesProofMap)
  }, [values?.payments])

  return (
    <div className={styles.core}>
      <Text variant="text-large-bold">{t('financeTabPaymentsTitle')}</Text>

      {values?.payments?.map((payment, index) => (
        <div className={styles.content} key={payment?.uuid}>
          <div className={styles['content-title']}>
            <Text variant="text-large">
              {getPaymentTitle(payment?.form, payment?.instalment)}
            </Text>
          </div>

          <div className={styles.field}>
            <Label>{t('paymentFormLabel')}</Label>
            <Select
              allowEmpty={false}
              options={
                values?.buyer?.financeProfile?.enabledPaymentForms?.map(
                  (payment) => ({
                    label: tEnums(`financial_profile_payment_forms_${payment}`),
                    value: payment,
                  }),
                ) || []
              }
              value={payment?.form}
              onChange={(newValue) =>
                onChangePaymentValues({
                  paymentUuid: payment.uuid,
                  keyIndex: index,
                  keyParam: 'form',
                  value: newValue,
                })
              }
            />
          </div>

          <div className={styles.field}>
            <Label>{t('paymentMethodLabel')}</Label>
            <Select
              allowEmpty={false}
              options={
                values?.buyer?.financeProfile?.enabledPaymentMethods?.map(
                  (payment) => ({
                    label: tEnums(
                      `financial_profile_transaction_payment_methods_${payment}`,
                    ),
                    value: payment,
                  }),
                ) || []
              }
              value={payment?.method}
              onChange={(newValue) =>
                onChangePaymentValues({
                  paymentUuid: payment.uuid,
                  keyIndex: index,
                  keyParam: 'method',
                  value: newValue,
                })
              }
            />
          </div>

          <div className={styles.field}>
            <Label>{t('paymentStatusLabel')}</Label>
            <Select
              options={[
                {
                  label: tEnums('order_payment_status_pending'),
                  value: 'pending',
                },
                {
                  label: tEnums('order_payment_status_waiting_approval'),
                  value: 'waiting_approval',
                },
                {
                  label: tEnums('order_payment_status_paid'),
                  value: 'paid',
                },
                {
                  label: tEnums('order_payment_status_invalid'),
                  value: 'invalid',
                },
                {
                  label: tEnums('order_payment_status_cancelled'),
                  value: 'cancelled',
                },
              ]}
              allowEmpty={false}
              value={payment?.status}
              onChange={(newValue) =>
                onChangePaymentValues({
                  paymentUuid: payment.uuid,
                  keyIndex: index,
                  keyParam: 'status',
                  value: newValue,
                })
              }
            />
          </div>

          <div className={styles.field}>
            <Label>{t('paymentDateLabel')}</Label>
            <DatePicker
              value={values?.payments?.[index]?.paidAt}
              onChange={(newValue) =>
                onChangePaymentValues({
                  keyIndex: index,
                  keyParam: 'paidAt',
                  value: newValue || '',
                  paymentUuid: payment.uuid,
                })
              }
            />
          </div>

          <div className={styles.field}>
            <Label>{t('paymentProofLabel')}</Label>
            <DragDropFileList
              translations={{
                clickToUpload: t('uploadClickToUpload'),
                dragFile: t('uploadDragFile'),
                sizeFile: t('uploadSizeFile'),
              }}
              urlAdd={ORDERS_CENTER_PAYMENT_URL(payment?.uuid || '')}
              configsRequest={{
                fileField: 'proof',
                method: 'PATCH',
              }}
              axiosInstance={api}
              data={dataFilesProofMap[payment?.uuid] || []}
              onChangeData={(
                newFiles:
                  | DragDropFileFileProps[]
                  | ((
                      prevFiles: DragDropFileFileProps[],
                    ) => DragDropFileFileProps[]),
              ) => {
                setDataFilesProofMap((prevMap) => ({
                  ...prevMap,
                  [payment?.uuid || '']: Array.isArray(newFiles)
                    ? newFiles
                    : newFiles(dataFilesProofMap[payment?.uuid] || []),
                }))
              }}
              successedUpload={onSuccessUploadFiles}
            />
          </div>
        </div>
      ))}

      <OrderPayment isLoading={false} saleOrder={values} />

      {!!financeValidationFormOpen && (
        <ModalFinanceValidation
          isOpen={!!financeValidationFormOpen}
          form={financeValidationFormOpen}
          saleOrderUuid={values?.uuid}
          onClose={handleCloseValidationModal}
        />
      )}
    </div>
  )
}
